import React, { useCallback, useMemo, useState } from "react";
import {
  Grid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor } from "@progress/kendo-data-query";
import { useArchivedPhones } from "./hooks/useArchivedPhones";
import { useAccountConfig } from "./hooks/useAccountConfig";
import Loading from "src/components/Loading";
import ScoreCell from "./components/ScoreCell";
import { getAttLabel } from "./utils/carrierDisplay";
import { RefreshPager, RefreshPagerProvider } from "./components/RefreshPager";

const ArchivedPhones: React.FC = () => {
  const config = useAccountConfig();

  const isCanadian = config.data?.isCanadian ?? false;
  const isTrustCallV3 = config.data?.isTrustCallVersion3 ?? false;

  // Grid state
  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "dateLastUpdate", dir: "desc" },
  ]);

  const sortField = sort[0]?.field || "dateLastUpdate";
  const sortDir = sort[0]?.dir || "desc";

  const archiveQuery = useArchivedPhones({
    page: Math.floor(page.skip / page.take) + 1,
    pageSize: page.take,
    sortBy: sortField,
    direction: sortDir,
  });

  const data = archiveQuery.data?.data ?? [];
  const total = archiveQuery.data?.total ?? 0;

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleRefresh = useCallback(() => { void archiveQuery.refetch(); }, [archiveQuery.refetch]);
  const refreshPagerValue = useMemo(
    () => ({ onRefresh: handleRefresh, isFetching: archiveQuery.isFetching }),
    [handleRefresh, archiveQuery.isFetching],
  );

  const handleExportCsv = () => {
    const apiUrl = import.meta.env.VITE_APP_API_URL;
    window.open(`${apiUrl}/trustcall/export/archive-csv`, "_blank");
  };

  return (
    <div>
      <h5 className="text-muted mb-2">Below are phone numbers that we have stopped monitoring</h5>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="flex-grow-1" />
        <button className="btn btn-outline-secondary btn-sm" onClick={handleExportCsv}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: 4, verticalAlign: "text-bottom" }}>
            <path d="M20.707 10.38v9.72a1.416 1.416 0 0 1-1.417 1.417H4.71A1.416 1.416 0 0 1 3.293 20.1v-9.72A1.416 1.416 0 0 1 4.71 8.963h2.43a.607.607 0 0 1 0 1.214H4.71a.204.204 0 0 0-.203.203v9.72a.204.204 0 0 0 .203.203h14.58a.204.204 0 0 0 .203-.203v-9.72a.204.204 0 0 0-.203-.203h-2.43a.608.608 0 0 1 0-1.215h2.43a1.416 1.416 0 0 1 1.417 1.417zM8.378 5.95l3.014-3.012v9.872a.608.608 0 0 0 1.215 0V2.938L15.62 5.95a.607.607 0 1 0 .86-.86l-4.05-4.05a.607.607 0 0 0-.86 0L7.52 5.09a.607.607 0 1 0 .86.86z" />
          </svg>
          Export All CSV
        </button>
      </div>

      <div style={{ position: "relative" }}>
        {archiveQuery.isFetching && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}>
            <Loading />
          </div>
        )}
      <RefreshPagerProvider value={refreshPagerValue}>
      <Grid
        data={data}
        total={total}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 25, 50, 100],
        }}
        pager={RefreshPager}
        skip={page.skip}
        take={page.take}
        onPageChange={handlePageChange}
        sortable={{ mode: "multiple", allowUnsort: true }}
        sort={sort}
        onSortChange={handleSortChange}
        resizable={true}
        reorderable={true}
        style={{ height: "calc(100vh - 240px)" }}
      >
        <Column field="phoneNumber" title="Number" minResizableWidth={240} />
        <Column field="label" title="Label" minResizableWidth={240} />
        <Column field="formattedDateRemove" title="Removed On" minResizableWidth={100} />
        <Column field="scamScore" title="Last Score" minResizableWidth={90} cell={ScoreCell} />
        <Column field="histScamScore" title="Last Historical Score" minResizableWidth={90} cell={ScoreCell} />
        <Column field="maxScamScore" title="Last Max Score" minResizableWidth={90} cell={ScoreCell} />

        {isTrustCallV3 && (
          <Column field="verizonScore" title="Verizon Score" minResizableWidth={90} cell={ScoreCell} />
        )}
        {isTrustCallV3 && (
          <Column field="attScore" title={`${getAttLabel(isCanadian)} Score`} minResizableWidth={90} cell={ScoreCell} />
        )}
        {isTrustCallV3 && (
          <Column field="tmobileScore" title="TMobile Score" minResizableWidth={90} cell={ScoreCell} />
        )}

        <Column field="region" title="Region" minResizableWidth={70} />
        <Column field="locale" title="Locale" minResizableWidth={70} />
        <Column field="ftcCount" title="FTC Complaints" minResizableWidth={100} />
        <Column field="serviceType" title="Service Type" minResizableWidth={100} />
      </Grid>
      </RefreshPagerProvider>
      </div>
    </div>
  );
};

export default ArchivedPhones;
